import React, { useEffect, useState } from 'react';
import { NextPageContext } from 'next';

import initializePageParams from 'src/page-params-initializer';
import dynamic from 'next/dynamic';
import Searchform from 'modules/searchform';
import useCarsApi from 'providers/cars-api/use-cars-api';
import Offerlist from 'modules/offerlist';
import { OffersProvider } from 'providers/offers';
import useTracking from 'providers/tracking/use-tracking';
import PageHeader from 'modules/page-header';
import { browserSide } from 'src/utils';
import useHasMounted from 'hooks/use-has-mounted';
import useBanners from 'providers/banners/use-banners-context';
import Banner from 'components/banner';
import { browserName, deviceType, mobileModel, mobileVendor, osName } from 'react-device-detect';
import Head from 'next/head';
import useTranslation from 'providers/translations/use-translations';

const Page = (props) => {
  const hasMounted = useHasMounted();
  const { t } = useTranslation();
  const { triggerSearch } = useTracking();
  const { subpage, general, agent } = useCarsApi();
  const [searchModified, setSearchModified] = useState(false);
  const { search } = props;
  const [CustomComponentsList] = useState({
    BahnDeAgentSwitch: null
  });

  const { banners, getBanners } = useBanners();

  if (['bahndecar', 'bahndebahncardcar'].includes(agent?.agentId)) {
    if (!CustomComponentsList.BahnDeAgentSwitch) {
      CustomComponentsList.BahnDeAgentSwitch = dynamic(
        () => import('components/custom-agent-components/bahnde-agent-switch'),
        { ssr: false }
      );
    }
  }

  useEffect(() => {
    if (subpage === 'searchform') {
      triggerSearch({
        language: search?.locale,
        ua_browser: browserName,
        ua_os: osName,
        ua_device:
          mobileVendor && mobileVendor !== 'none'
            ? `${deviceType} / ${mobileVendor} ${mobileModel}`
            : deviceType
      });
      getBanners({
        agent: agent.agentId,
        IBEStep: 'search'
      });
    }
  }, [subpage]);

  return (
    <>
      <a href="#main" className="skip-to-content-link">
        {t('aria_skip_to_main')}
      </a>
      <Head>
        <title>
          {subpage === 'searchform' ? t('aria_page_searchform') : t('aria_page_offerlist')}
        </title>
      </Head>

      <OffersProvider agent={agent} general={general}>
        <div className="index__custom-components">
          {hasMounted && CustomComponentsList.BahnDeAgentSwitch && browserSide() && (
            <CustomComponentsList.BahnDeAgentSwitch />
          )}
        </div>
        <PageHeader
          pageName={subpage === 'searchform' ? t('aria_page_searchform') : t('aria_page_offerlist')}
        />
        <>
          {banners && <Banner banners={banners} alignment="top" />}
          <Searchform
            search={search}
            isSearchModified={setSearchModified}
            mainContent={subpage !== 'offerlist'}
          />
          {banners && <Banner banners={banners} alignment="left" />}
          {banners && <Banner banners={banners} alignment="right" />}
          {subpage === 'offerlist' && !searchModified && <Offerlist />}
          {banners && <Banner banners={banners} alignment="bottom" />}
        </>
      </OffersProvider>
    </>
  );
};

export const getServerSideProps = async (ctx: NextPageContext): Promise<any> => ({
  props: {
    ...(await initializePageParams({
      ctx,
      subpage: 'searchform',
      languageNamespaces: [
        'all_pages',
        'car_details',
        'search_form',
        '2_page',
        'misc',
        'charges',
        'filters',
        'insurances',
        'partners',
        'date_and_time'
      ]
    }).catch(() => null))
  }
});

export default Page;
